// @adr SPARK-0055-document-displays-use-one-public-boundary@2: Public entry point for existing document display; generator editors are explicitly separate.
import { useMemo, type ComponentProps, type ReactNode } from "react";
import { createInMemoryDocumentResource, type DocumentResource, type InMemoryDocument } from "@/lib/document-resource";
import { DocumentTile } from "./DocumentTile";
import { DocumentImage } from "./DocumentImage";
import { DocumentRow } from "./DocumentRow";
import { DocumentInline } from "./DocumentInline";
import { DocumentPreview } from "./DocumentPreview";
import { DocItemRow } from "./DocItemRow";
import { DocPreviewCard } from "./DocPreviewCard";

export type DocumentFile = DocumentResource | InMemoryDocument;
type FileProps = { file: DocumentFile };
type RowProps = Omit<ComponentProps<typeof DocItemRow>, "resource">;
type CardProps = Omit<ComponentProps<typeof DocPreviewCard>, "resource">;
export type DocumentViewProps =
  | (FileProps & { variant: "tile" } & Omit<ComponentProps<typeof DocumentTile>, "file">)
  | (FileProps & { variant: "image" } & Omit<ComponentProps<typeof DocumentImage>, "file">)
  | (FileProps & { variant: "row" } & Omit<ComponentProps<typeof DocumentRow>, "file">)
  | (FileProps & { variant?: "inline" } & Omit<ComponentProps<typeof DocumentInline>, "file">)
  | (FileProps & { variant: "viewer"; onClose?: () => void })
  | (FileProps & { variant: "library-row" } & RowProps)
  | (FileProps & { variant: "library-card" } & CardProps)
  | ({ variant: "editor-row"; file?: never } & RowProps)
  | ({ variant: "editor-card"; file?: never } & CardProps);

/** The public file display boundary: bytes/capability and preview UI cannot be split by consumers. */
export function DocumentView(props: DocumentViewProps): ReactNode {
  const file = props.file;
  const resource = useMemo(() => file ? "previewable" in file ? file : createInMemoryDocumentResource(file) : undefined, [file]);
  switch (props.variant) {
    case "editor-row": return <DocItemRow {...props} editor />;
    case "editor-card": return <DocPreviewCard {...props} editor cvGenerator />;
    case "library-row": return <DocItemRow {...props} resource={resource!} />;
    case "library-card": return <DocPreviewCard {...props} resource={resource!} />;
    case "image": return <DocumentImage {...props} file={resource!} />;
    case "row": return <DocumentRow {...props} file={resource!} />;
    case "tile": return <DocumentTile {...props} file={resource!} />;
    case "viewer": return <DocumentPreview resource={resource!} onClose={props.onClose} />;
    default: return <DocumentInline {...props} file={resource!} />;
  }
}
